import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useShiftPunches } from "../../../../hooks/useShiftPunches";
import { FONT_SIZE, GRID_RADIUS, INK, NEUTRAL, STATUS, SURFACE } from "../../../../theme/tokens";
import { formatIsoDateTime } from "../../../../utils/dateUtils";
import { PunchEventType, type ShiftPunchEvent } from "../../../../utils/models";
import { buildShiftChangelogPath } from "../../../../utils/shiftChangelogLink";
import { JumpingDots } from "../../Animations/JumpingDots";
import { EmptyState } from "../../molecules/EmptyState";
import { LabeledField } from "../../molecules/LabeledField";
import SystemButton from "../../SystemButton/SystemButton";
import SystemDialog from "../Dialog";

type Props = {
	/** The actual shift id (nurse_in_shift_actual id) whose raw punches to load. */
	shiftId: string;
	/**
	 * The shift's nurse. When given, the dialog offers the jump to that shift's SYNC CHANGE LOG — "these
	 * are the punches; here is what the sync decided about them and why". Omit on a surface that can't
	 * name the employee and the dialog stays a plain punch list.
	 */
	nurseId?: number;
	open: boolean;
	onClose: () => void;
};

const PunchDirectionMarker: FC<{ eventType: ShiftPunchEvent["eventType"]; label: string }> = ({ eventType, label }) => {
	const color = eventType === PunchEventType.In ? STATUS.SUCCESS.main : eventType === PunchEventType.Out ? STATUS.ERROR.main : INK.MUTED;
	const Icon = eventType === PunchEventType.In ? LoginIcon : eventType === PunchEventType.Out ? LogoutIcon : RemoveIcon;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: "56px", color }} aria-label={label}>
			<Icon sx={{ fontSize: FONT_SIZE.TITLE_MD }} />
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 700, textTransform: "uppercase" }}>{label}</Typography>
		</Box>
	);
};

const PunchRow: FC<{ punch: ShiftPunchEvent; index: number }> = ({ punch, index }) => {
	const { t } = useTranslation();
	// DAT-607: collapse each punch to its type + time by default; details expand on demand.
	const [expanded, setExpanded] = useState(false);
	const directionLabel =
		punch.eventType === PunchEventType.In
			? t("shift.punches.type.in")
			: punch.eventType === PunchEventType.Out
				? t("shift.punches.type.out")
				: t("shift.punches.type.unknown");

	// One config row per persisted PunchEvent field — mapped below so adding a field is a one-line change.
	const fields: { label: string; value: string | number | null }[] = [
		{ label: t("profile.integrations.field.source"), value: punch.source },
		{ label: t("shift.punches.field.rawType"), value: punch.rawEventType },
		{ label: t("job"), value: punch.job },
		{ label: t("shift.punches.field.hrisDepartmentId"), value: punch.hrisDepartmentId },
		{ label: t("profile.integrations.field.externalId"), value: punch.externalEmployeeId },
		{ label: t("shift.punches.field.departmentName"), value: punch.departmentName },
		{ label: t("shift.punches.field.earnCode"), value: punch.earnCode },
		{ label: t("shift.punches.field.earnHours"), value: punch.earnHours },
		{ label: t("shift.punches.field.rawEarningCodes"), value: punch.rawEarningCodes?.join(", ") ?? null },
		{ label: t("shift.punches.field.agencyName"), value: punch.agencyName },
		{ label: t("shift.punches.field.distJobCode"), value: punch.distJobCode },
		{ label: t("shift.punches.field.priority"), value: punch.priority },
		{ label: t("shift.punches.field.pairToken"), value: punch.pairToken },
	];

	return (
		<Box
			data-testid={`raw-punches-row-${index}`}
			sx={{
				borderRadius: GRID_RADIUS.PANEL,
				border: `1px solid ${NEUTRAL[200]}`,
				backgroundColor: SURFACE,
				overflow: "hidden",
			}}
		>
			<ButtonBase
				onClick={() => setExpanded((prev) => !prev)}
				aria-expanded={expanded}
				aria-label={expanded ? t("system.dataGrid.collapseRow") : t("system.dataGrid.expandRow")}
				data-testid={`raw-punches-row-toggle-${index}`}
				sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5, py: 1.25, px: 1.5, justifyContent: "flex-start", textAlign: "start" }}
			>
				<PunchDirectionMarker eventType={punch.eventType} label={directionLabel} />
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.SECONDARY }}>
					{/* eventTime is nullable for dangling punches — LabeledField-style fallback handled inline. */}
					{punch.eventTime ? formatIsoDateTime(punch.eventTime) : "--"}
				</Typography>
				<KeyboardArrowDownIcon
					sx={{
						ml: "auto",
						color: INK.MUTED,
						fontSize: FONT_SIZE.TITLE_MD,
						transition: "transform 0.2s ease",
						transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
					}}
				/>
			</ButtonBase>
			<Collapse in={expanded} timeout={200} unmountOnExit>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
						rowGap: 1,
						columnGap: 2,
						px: 1.5,
						pb: 1.25,
					}}
				>
					{fields.map((field) => (
						<LabeledField key={field.label} label={field.label} value={field.value} />
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

/**
 * DAT-221: SUPPORT-ONLY dialog listing every raw HRIS punch event behind a shift, in chronological
 * order (the backend orders by eventTime asc), with its source, direction, raw event type, job,
 * earn codes/hours and external identifiers. No anomaly flagging (v1) — a plain audit list.
 *
 * Generic over any shift id: pass `shiftId` + open/close control. The support gate lives in
 * `useShiftPunches` (the query is disabled for non-support users), so this can be mounted behind a
 * support-only trigger without leaking the endpoint. Handles loading / empty / error.
 */
export const RawPunchesDialog: FC<Props> = ({ shiftId, nurseId, open, onClose }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { data, isLoading, error } = useShiftPunches(open ? shiftId : "");

	useEffect(() => {
		if (open) track("raw_punches_dialog_opened", { shift_id: shiftId });
	}, [open, shiftId, track]);

	/** The natural next question after "what were the raw punches": what did the sync DO with them. */
	const openSyncChangeLog = () => {
		if (nurseId === undefined || !chosenDepartment?.department_id) return;
		track("raw_punches_open_sync_change_log", { shift_id: shiftId, nurse_id: nurseId, department_id: chosenDepartment.department_id });
		onClose();
		navigate(buildShiftChangelogPath({ departmentId: chosenDepartment.department_id, nurseId, shiftId }));
	};

	const body = (() => {
		if (isLoading) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<JumpingDots />
				</Box>
			);
		}
		if (error) {
			return (
				<Box sx={{ py: 2 }} data-testid="raw-punches-error">
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: STATUS.ERROR.main, textAlign: "center" }}>{t("shift.punches.error")}</Typography>
				</Box>
			);
		}
		if (!data || data.length === 0) {
			return <EmptyState title={t("shift.punches.empty")} dataTestId="raw-punches-empty" />;
		}
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pt: 1, minWidth: { sm: "440px" } }}>
				{data.map((punch, index) => (
					<PunchRow key={punch.id} punch={punch} index={index} />
				))}
			</Box>
		);
	})();

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("shift.punches.modal.title")}
			hideCancel
			confirm={t("close")}
			onConfirm={onClose}
			dataTestId="raw-punches-dialog"
			body={
				<>
					{/* Offered even when the list is EMPTY or errored — "there are no punches" is precisely when
					    the reader needs the sync's own account of what it decided. */}
					{nurseId !== undefined && (
						<SystemButton
							buttonVariant="secondary"
							size="small"
							leadingIcon={<HistoryToggleOffOutlinedIcon fontSize="small" />}
							onClick={openSyncChangeLog}
							tooltip={t("shift.punches.syncChangeLog.tooltip")}
							data-testid="raw-punches-open-change-log"
							noFullWidthMobile
						>
							{t("shift.punches.syncChangeLog.cta")}
						</SystemButton>
					)}
					{body}
				</>
			}
		/>
	);
};
